import axios from "axios";

export const COMPANY_SMTP_ACCOUNTS_ENDPOINT = "/company-email/smtp/accounts";

export const COMPANY_SMTP_PROVIDER_PRESETS = Object.freeze([
  {
    value: "generic",
    labelKey: "ncrm.integrations.smtp.providers.generic",
    host: "",
    port: 587,
    secure: false,
    requireTLS: true,
    authType: "password",
  },
  {
    value: "gmail",
    labelKey: "ncrm.integrations.smtp.providers.gmail",
    host: "smtp.gmail.com",
    port: 587,
    secure: false,
    requireTLS: true,
    authType: "password",
  },
  {
    value: "gmail_ssl",
    labelKey: "ncrm.integrations.smtp.providers.gmailSsl",
    host: "smtp.gmail.com",
    port: 465,
    secure: true,
    requireTLS: false,
    authType: "password",
  },
  {
    value: "google_workspace_relay",
    labelKey: "ncrm.integrations.smtp.providers.googleWorkspaceRelay",
    host: "smtp-relay.gmail.com",
    port: 587,
    secure: false,
    requireTLS: true,
    authType: "password",
  },
]);

const toBoolean = (value) => value === true || value === 1 || value === "1";

const trimOrNull = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed || null;
};

export const getCompanySmtpProviderPreset = (providerKey) =>
  COMPANY_SMTP_PROVIDER_PRESETS.find((preset) => preset.value === providerKey) ||
  COMPANY_SMTP_PROVIDER_PRESETS[0];

export const createCompanySmtpDraft = () => ({
  id: null,
  name: "",
  description: "",
  providerKey: "generic",
  isDefault: false,
  status: "active",
  host: "",
  port: 587,
  secure: false,
  requireTLS: true,
  authType: "password",
  username: "",
  password: "",
  fromEmail: "",
  fromName: "",
  replyToEmail: "",
});

export const applyCompanySmtpProviderPreset = (draft, providerKey) => {
  const preset = getCompanySmtpProviderPreset(providerKey);
  return {
    ...draft,
    providerKey: preset.value,
    host: preset.host || draft.host || "",
    port: preset.port || draft.port || 587,
    secure: Boolean(preset.secure),
    requireTLS: Boolean(preset.requireTLS),
    authType: preset.authType || draft.authType || "password",
  };
};

export const normalizeCompanySmtpAccount = (account = {}) => ({
  ...account,
  id: Number(account.id),
  is_default: toBoolean(account.is_default),
  secure: toBoolean(account.secure),
  require_tls: toBoolean(account.require_tls),
  port: Number(account.port),
});

export const buildCompanySmtpPayload = (draft, { includePassword = false } = {}) => {
  const authType = draft.authType === "none" ? "none" : "password";
  const payload = {
    name: String(draft.name || "").trim(),
    description: trimOrNull(draft.description),
    providerKey: draft.providerKey || "generic",
    isDefault: Boolean(draft.isDefault),
    status: draft.status || "active",
    host: String(draft.host || "").trim(),
    port: Number(draft.port),
    secure: Boolean(draft.secure),
    requireTLS: Boolean(draft.requireTLS),
    authType,
    username: authType === "password" ? trimOrNull(draft.username) : null,
    fromEmail: String(draft.fromEmail || "").trim(),
    fromName: trimOrNull(draft.fromName),
    replyToEmail: trimOrNull(draft.replyToEmail),
  };

  if (authType === "password" && includePassword) {
    payload.password = String(draft.password || "").trim();
  }

  return payload;
};

export const fetchCompanySmtpAccounts = () => axios.get(COMPANY_SMTP_ACCOUNTS_ENDPOINT);

export const createCompanySmtpAccount = (payload) =>
  axios.post(COMPANY_SMTP_ACCOUNTS_ENDPOINT, payload);

export const updateCompanySmtpAccount = (id, payload) =>
  axios.patch(`${COMPANY_SMTP_ACCOUNTS_ENDPOINT}/${id}`, payload);

export const deleteCompanySmtpAccount = (id) =>
  axios.delete(`${COMPANY_SMTP_ACCOUNTS_ENDPOINT}/${id}`);

export const testCompanySmtpAccount = (id) =>
  axios.post(`${COMPANY_SMTP_ACCOUNTS_ENDPOINT}/${id}/test`, {}, { validateStatus: () => true });

export const sendCompanySmtpTestEmail = (id, to) =>
  axios.post(`${COMPANY_SMTP_ACCOUNTS_ENDPOINT}/${id}/send-test`, { to });
